/* ============================================================
   FERAL 2026 — Collection page (PLP) JS
   - Sticky toolbar reveal (IO on sentinel)
   - Reveal stagger on cards
   - Filter drawer open/close + AJAX filter/sort with section_id render
   - Variant swatch image swap
   - Quickview modal: fetch /products/<handle>.js, variant pills, AJAX add
   - Load-more pagination (button + auto when near bottom)
   - View density toggle (3 / 4 cols desktop)
   ============================================================ */
(function () {
  'use strict';

  const root = document.querySelector('[data-fpl-root]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const collectionUrl = root.dataset.collectionUrl;

  // Order-independent canonical form of a query string, so popstate compares by
  // FILTER STATE, not raw text — "?b=2&a=1" and "?a=1&b=2" are the same render.
  function canonicalSearch(search) {
    const pairs = [];
    for (const [k, v] of new URLSearchParams(search)) pairs.push(k + '=' + v);
    pairs.sort();
    return pairs.join('&');
  }

  // The query (filters / sort / page) currently rendered into the grid. popstate
  // compares against this so a back-navigation that DIDN'T change the query
  // (i.e. returning from a product page) skips the destructive grid re-render and
  // lets the browser keep its bfcache/native-restored scroll position.
  let lastRenderedSearch = canonicalSearch(window.location.search);

  // ────────────────────────────────────────────────────────────
  // SCROLL RESTORATION — own it explicitly. Returning from a product page must
  // land the shopper exactly where they left off, on every device. The browser's
  // native / bfcache restore proved flaky here (it would restore, then the page
  // bounced to the top). We record scrollY per collection URL and re-apply it —
  // instantly, and re-assert it every frame for ~1s — so a late layout shift or
  // any stray scroll-to-top can't win. It backs off the moment the shopper
  // scrolls/touches, so it never fights them. Pairs with the popstate guard
  // above (which already stops the destructive grid re-fetch on back-nav).
  // ────────────────────────────────────────────────────────────
  (function setupScrollMemory() {
    const skey = function () { return 'fpl:y:' + location.pathname + location.search; };
    let saveQueued = false;
    function save() { try { sessionStorage.setItem(skey(), String(Math.round(window.scrollY))); } catch (e) { /* private mode / quota */ } }
    window.addEventListener('scroll', function () {
      if (saveQueued) return;
      saveQueued = true;
      requestAnimationFrame(function () { save(); saveQueued = false; });
    }, { passive: true });
    // Capture the final position right before the page is hidden / navigated away.
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') save(); });

    let gen = 0;
    function restore() {
      let raw;
      try { raw = sessionStorage.getItem(skey()); } catch (e) { return; }
      const target = parseInt(raw, 10);
      if (!(target > 0)) return;
      const myGen = ++gen; // a newer restore() (or a user scroll) supersedes this run
      const bail = function () { gen++; };
      window.addEventListener('wheel', bail, { passive: true, once: true });
      window.addEventListener('touchstart', bail, { passive: true, once: true });
      window.addEventListener('keydown', bail, { once: true });
      const start = Date.now();
      (function hold() {
        if (myGen !== gen) return; // superseded or the shopper took over
        if (Math.abs(window.scrollY - target) > 2) window.scrollTo(0, target);
        if (Date.now() - start < 1000) requestAnimationFrame(hold);
      })();
    }
    // bfcache restore fires pageshow; a fresh reload runs this IIFE — cover both.
    window.addEventListener('pageshow', restore);
    restore();
  })();

  // ────────────────────────────────────────────────────────────
  // SYNC --header-h so the sticky toolbar tucks under the real header.
  // The CSS used a hard-coded 60px fallback, but the actual header runs
  // ~80–100px (announcement bar + nav row), causing the toolbar to overlap
  // the header bottom. Measure once on init and on resize / announcement
  // collapse so the offset is always exact.
  // ────────────────────────────────────────────────────────────
  function syncHeaderHeight() {
    const headerEl = document.querySelector('header.shopify-section') || document.querySelector('header.feral-h-section') || document.querySelector('header');
    if (!headerEl) return;
    const h = Math.round(headerEl.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--header-h', h + 'px');
  }
  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);
  if (typeof ResizeObserver !== 'undefined') {
    const headerEl = document.querySelector('header.shopify-section') || document.querySelector('header');
    if (headerEl) new ResizeObserver(syncHeaderHeight).observe(headerEl);
  }

  // (Reveal stagger is purely CSS-driven now — `animation: fpl-card-in` on
  // every `.fpl-grid__cell` with delay = `min(--fpl-i, 12) * 35ms`.)

  // ────────────────────────────────────────────────────────────
  // VARIANT SWATCHES — swap primary image on hover/click
  // ────────────────────────────────────────────────────────────
  root.addEventListener('mouseover', (e) => {
    const sw = e.target.closest('[data-fpl-swatch]');
    if (!sw) return;
    const card = sw.closest('[data-fpl-card]');
    if (!card) return;
    const img = card.querySelector('[data-fpl-card-img]');
    if (!img) return;
    const newSrc = sw.dataset.img;
    if (newSrc && img.src !== newSrc) {
      if (!img.dataset.origSrc) img.dataset.origSrc = img.src;
      img.src = newSrc;
      img.removeAttribute('srcset');
    }
  });
  root.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-fpl-swatch]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('[data-fpl-card]');
    if (!card) return;
    card.querySelectorAll('[data-fpl-swatch]').forEach((s) => s.classList.toggle('is-active', s === sw));
  });

  // ────────────────────────────────────────────────────────────
  // PORTAL drawer to <body> — it's inside .shopify-section which creates
  // a stacking context that traps high z-index from clearing the sticky
  // site header. Reparenting on init dodges the trap.
  // ────────────────────────────────────────────────────────────
  const drawerEl = document.querySelector('[data-fpl-drawer]');
  if (drawerEl && drawerEl.parentElement !== document.body) document.body.appendChild(drawerEl);

  // ────────────────────────────────────────────────────────────
  // FILTER DRAWER — open/close + body lock
  // ────────────────────────────────────────────────────────────
  const drawer = document.querySelector('[data-fpl-drawer]');
  const filterOpenBtn = root.querySelector('[data-fpl-filter-open]');

  function lockScroll(lock) { document.body.classList.toggle('fpl-locked', !!lock); }
  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
  }
  if (filterOpenBtn) filterOpenBtn.addEventListener('click', openDrawer);
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-fpl-drawer-close]')) closeDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
  }

  // Active filter count badge in the toolbar
  function updateActiveCount() {
    const badge = root.querySelector('[data-fpl-active-count]');
    if (!badge || !drawer) return;
    const checked = drawer.querySelectorAll('input[type="checkbox"]:checked').length;
    const priceInputs = drawer.querySelectorAll('input[type="number"]');
    let priceActive = 0;
    priceInputs.forEach((i) => { if (i.value) priceActive = 1; });
    const total = checked + priceActive;
    if (total > 0) {
      badge.hidden = false;
      badge.textContent = total;
    } else {
      badge.hidden = true;
    }
  }
  updateActiveCount();

  // ────────────────────────────────────────────────────────────
  // AJAX FILTER + SORT — fetch with section_id, replace grid
  // ────────────────────────────────────────────────────────────
  const sortSelect = root.querySelector('[data-fpl-sort]');
  const sortHidden = drawer ? drawer.querySelector('[data-fpl-sort-input]') : null;
  const filterForm = drawer ? drawer.querySelector('[data-fpl-filter-form]') : null;

  function buildSearchFromForm(form) {
    const fd = new FormData(form);
    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) {
      if (v === '' || v == null) continue;
      params.append(k, v);
    }
    return params;
  }

  async function fetchAndReplace(searchParams, { pushState = true, scrollToTop = false } = {}) {
    const url = collectionUrl + '?' + searchParams.toString();
    const sectionFetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + sectionId;

    const wrap = root.querySelector('[data-fpl-grid-wrap]');
    if (wrap) wrap.classList.add('is-fetching');

    try {
      const r = await fetch(sectionFetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGridWrap = doc.querySelector('[data-fpl-grid-wrap]');
      const newChips = doc.querySelector('[data-fpl-chips]');
      const newCount = doc.querySelector('[data-fpl-count]');
      const newDrawerBody = doc.querySelector('[data-fpl-filters]');

      const curGridWrap = root.querySelector('[data-fpl-grid-wrap]');
      const curChips = root.querySelector('[data-fpl-chips]');
      const curCount = root.querySelector('[data-fpl-count]');
      const curDrawerBody = drawer ? drawer.querySelector('[data-fpl-filters]') : null;

      if (newGridWrap && curGridWrap) curGridWrap.innerHTML = newGridWrap.innerHTML;
      if (newChips && curChips) curChips.outerHTML = newChips.outerHTML;
      if (newCount && curCount) curCount.textContent = newCount.textContent;
      if (newDrawerBody && curDrawerBody) curDrawerBody.innerHTML = newDrawerBody.innerHTML;

      updateActiveCount();
      if (pushState) history.pushState({}, '', url);
      lastRenderedSearch = canonicalSearch(window.location.search);
      if (scrollToTop) {
        const w = root.querySelector('[data-fpl-grid-wrap]');
        if (w) {
          const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 60;
          const top = w.getBoundingClientRect().top + window.scrollY - headerH - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    } catch (err) {
      console.error('PLP fetch failed', err);
    } finally {
      // Use rAF to let the freshly-inserted cards register before fading back
      const w = root.querySelector('[data-fpl-grid-wrap]');
      if (w) requestAnimationFrame(() => w.classList.remove('is-fetching'));
    }
  }

  // Sort: change → fetch
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const sortVal = sortSelect.value;
      if (sortHidden) sortHidden.value = sortVal;
      // Build params from existing URL + new sort
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', sortVal);
      params.delete('page');
      fetchAndReplace(params, { scrollToTop: false });
    });
  }

  // Filter form submit → fetch
  if (filterForm) {
    filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      // Sync sort_by from sort select
      if (sortHidden && sortSelect) sortHidden.value = sortSelect.value;
      const params = buildSearchFromForm(filterForm);
      params.delete('page');
      fetchAndReplace(params, { scrollToTop: true });
      closeDrawer();
    });

    // Live result count preview
    let liveTimer;
    filterForm.addEventListener('change', () => {
      updateActiveCount();
      clearTimeout(liveTimer);
      liveTimer = setTimeout(async () => {
        const params = buildSearchFromForm(filterForm);
        params.delete('page');
        const sectionFetchUrl = collectionUrl + '?' + params.toString() + '&section_id=' + sectionId;
        try {
          const r = await fetch(sectionFetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          const html = await r.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newCount = doc.querySelector('[data-fpl-count]');
          const apply = drawer.querySelector('[data-fpl-result-count]');
          if (newCount && apply) {
            const num = (newCount.textContent.match(/\d+/) || ['0'])[0];
            apply.textContent = num;
          }
        } catch (e) { /* noop */ }
      }, 250);
    });
  }

  // Active filter chip remove (event delegation; works for replaced markup)
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-fpl-chip-remove]');
    if (!chip) return;
    e.preventDefault();
    const href = chip.getAttribute('href');
    if (!href) return;
    const u = new URL(href, window.location.origin);
    fetchAndReplace(u.searchParams, { scrollToTop: false });
  });

  // ────────────────────────────────────────────────────────────
  // LOAD MORE — explicit click only. No auto-IO infinite scroll:
  // it loops on itself when the new pagination element re-enters
  // viewport, the stagger animation batches synchronously past index
  // 12, and the back button can't restore scroll position. Manual
  // click is the SSENSE / Bottega / ALD pattern and reads premium.
  // ────────────────────────────────────────────────────────────
  async function loadMore(btn) {
    if (!btn || btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    const label = btn.querySelector('[data-fpl-load-label]');
    const originalLabel = label ? label.textContent : '';
    if (label) label.textContent = 'Loading';

    const nextUrl = btn.getAttribute('href');
    const sectionFetchUrl = nextUrl + (nextUrl.includes('?') ? '&' : '?') + 'section_id=' + sectionId;

    try {
      const r = await fetch(sectionFetchUrl);
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = root.querySelector('[data-fpl-grid]');
      const newNodes = [...doc.querySelectorAll('[data-fpl-cell], [data-fpl-break]')];

      // Reset stagger index PER BATCH (not absolute). Without this every newly
      // loaded card past index 12 hits the cap and animates simultaneously.
      newNodes.forEach((c, idx) => {
        if (c.style) c.style.setProperty('--fpl-i', String(idx));
        grid.appendChild(c);
      });

      // Update the counter + bar IN PLACE rather than replacing the whole block.
      // A replaced bar is a brand-new element, so it paints straight at its
      // final width and the `transition: width` already in the CSS never fires.
      // Writing into the live nodes lets the bar actually travel and the count
      // tick under it. Only the button's existence changes between renders —
      // on the last page there is no `paginate.next`, so it goes away.
      const newPag = doc.querySelector('[data-fpl-pagination]');
      const curPag = root.querySelector('[data-fpl-pagination]');
      if (newPag && curPag) {
        ['[data-fpl-shown]', '[data-fpl-total]'].forEach((sel) => {
          const from = newPag.querySelector(sel);
          const to = curPag.querySelector(sel);
          if (from && to) to.textContent = from.textContent;
        });

        const newBar = newPag.querySelector('[data-fpl-bar]');
        const curBar = curPag.querySelector('[data-fpl-bar]');
        if (newBar && curBar) curBar.style.setProperty('--p', newBar.style.getPropertyValue('--p'));

        const newMore = newPag.querySelector('[data-fpl-load-more]');
        if (newMore) btn.replaceWith(newMore);
        else btn.remove();
      } else if (curPag) {
        curPag.remove();
      }

      history.replaceState({}, '', nextUrl);
      lastRenderedSearch = canonicalSearch(window.location.search);
    } catch (err) {
      console.error('PLP load-more failed', err);
      btn.classList.remove('is-loading');
      if (label) label.textContent = originalLabel || 'Load more';
    }
  }

  // Click on load-more (event delegation — replaced after each load)
  root.addEventListener('click', (e) => {
    const more = e.target.closest('[data-fpl-load-more]');
    if (more) {
      e.preventDefault();
      loadMore(more);
    }
  });

  // ────────────────────────────────────────────────────────────
  // QUICK-ADD — desktop hover size pills, click → /cart/add.js
  // Mirrors the existing product-form flow so cart-drawer renders correctly:
  //  - POST form-encoded to /cart/add.js with id, quantity, and `sections`
  //    (= the cart drawer's section IDs) so the response includes pre-rendered
  //    HTML chunks
  //  - On success, hand the response to cart.renderContents() to swap in the
  //    new contents AND open the drawer; same publish() event so anything else
  //    subscribed to cartUpdate (cart count bubble, etc.) refreshes
  // ────────────────────────────────────────────────────────────
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-fpl-qa-size]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.classList.contains('is-soldout') || btn.classList.contains('is-loading')) return;

    const card = btn.closest('[data-fpl-card]');
    if (!card) return;
    const dataEl = card.querySelector('[data-fpl-variants]');
    if (!dataEl) return;

    let info;
    try { info = JSON.parse(dataEl.textContent); } catch (_) { return; }

    const size = btn.dataset.fplQaSize;

    let activeColor = null;
    if (info.color_index >= 0) {
      const sw = card.querySelector('[data-fpl-swatch].is-active') || card.querySelector('[data-fpl-swatch]');
      if (sw) activeColor = (sw.dataset.color || '').toLowerCase().trim();
    }

    const variant = info.variants.find((v) => {
      if (info.size_index < 0) return false;
      if (v.options[info.size_index] !== size) return false;
      if (info.color_index >= 0 && activeColor) {
        const vc = (v.options[info.color_index] || '').toLowerCase().trim();
        if (vc !== activeColor) return false;
      }
      return v.available;
    });

    if (!variant) {
      btn.style.borderColor = 'var(--fpl-blood)';
      setTimeout(() => { btn.style.borderColor = ''; }, 1200);
      return;
    }

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    btn.classList.add('is-loading');
    try {
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', '1');

      // Provide the sections the cart drawer wants pre-rendered in the response
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
        body: formData,
      });
      const data = await res.json();

      if (data.status) {
        // Shopify error response (e.g., 422 sold out) — non-success
        throw new Error(data.description || data.message || 'Add failed');
      }

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1400);

      if (cart) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        if (typeof cart.renderContents === 'function') {
          cart.renderContents(data);
        }
      }

      // Notify any other listeners (cart-count bubbles, etc.)
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fpl-quickadd', productVariantId: String(variant.id), cartData: data });
      }
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.style.borderColor = 'var(--fpl-blood)';
      setTimeout(() => { btn.style.borderColor = ''; }, 1500);
      console.error('PLP quick-add failed:', err);
    }
  });

  // ────────────────────────────────────────────────────────────
  // MOBILE QUICK-ADD — basket trigger → size bottom-sheet → /cart/add.js
  // Touch UX (desktop uses the hover size pills above). One shared sheet,
  // portaled to <body>, populated per-card on open. Reuses the same cart-add
  // + cart-drawer render flow as the desktop pills.
  // ────────────────────────────────────────────────────────────
  (function fplMobileQuickAdd() {
    // Markup MIRRORS the PDP "Add to bag" sheet (sections/feral-product.liquid)
    // so the quick-add is the exact same UX/UI as the product page.
    const sheet = document.createElement('div');
    sheet.className = 'fp__sheet';
    sheet.setAttribute('data-fpl-qa-sheet', '');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', 'Select your size');
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="fp__sheet-overlay" data-fpl-qa-close>&nbsp;</div>' +
      '<div class="fp__sheet-panel" role="document">' +
        '<header class="fp__sheet-head">' +
          '<span class="fp__sheet-title">Add to bag</span>' +
          '<button type="button" class="fp__sheet-close" data-fpl-qa-close aria-label="Close">' +
            '<svg viewBox="0 0 14 14" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12"/></svg>' +
          '</button>' +
        '</header>' +
        '<div class="fp__sheet-product" data-fpl-qa-product></div>' +
        '<p class="fp__sheet-list-label">Select size</p>' +
        '<div class="fp__sheet-list" data-fpl-qa-list></div>' +
      '</div>';
    document.body.appendChild(sheet);
    const productEl = sheet.querySelector('[data-fpl-qa-product]');
    const listEl = sheet.querySelector('[data-fpl-qa-list]');
    const esc = (s) => String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));

    const openSheet = () => {
      sheet.hidden = false;
      document.documentElement.classList.add('fp-sheet-locked');
      requestAnimationFrame(() => sheet.classList.add('is-open'));
    };
    const closeSheet = () => {
      sheet.classList.remove('is-open');
      document.documentElement.classList.remove('fp-sheet-locked');
      setTimeout(() => { if (!sheet.classList.contains('is-open')) sheet.hidden = true; }, 360);
    };

    // Basket trigger → resolve this card's sizes → populate + open sheet.
    document.addEventListener('click', (e) => {
      const trig = e.target.closest('[data-fpl-qa-trigger]');
      if (!trig) return;
      e.preventDefault();
      e.stopPropagation();
      const card = trig.closest('[data-fpl-card]');
      if (!card) return;
      const dataEl = card.querySelector('[data-fpl-variants]');
      if (!dataEl) return;
      let info;
      try { info = JSON.parse(dataEl.textContent); } catch (_) { return; }
      if (info.size_index < 0) return;

      // Product block (thumb + name + price) from the card — like the PDP sheet.
      const img = card.querySelector('.fpl-card__img--primary');
      const titleA = card.querySelector('.fpl-card__title');
      const priceNow = card.querySelector('.fpl-card__price-now');
      const linkA = (titleA && titleA.querySelector('a')) || card.querySelector('.fpl-card__link');
      const url = linkA ? linkA.getAttribute('href') : (card.dataset.handle ? '/products/' + card.dataset.handle : '#');
      const name = titleA ? titleA.textContent.trim() : '';
      const thumb = img ? (img.currentSrc || img.src) : '';
      productEl.innerHTML =
        (thumb ? '<img class="fp__sheet-thumb" src="' + thumb + '" alt="" width="76" height="92" loading="lazy">' : '') +
        '<div class="fp__sheet-info">' +
          '<p class="fp__sheet-name">' + esc(name) + '</p>' +
          (priceNow ? '<p class="fp__sheet-price">' + esc(priceNow.textContent.trim()) + '</p>' : '') +
        '</div>';

      let activeColor = null;
      if (info.color_index >= 0) {
        const sw = card.querySelector('[data-fpl-swatch].is-active') || card.querySelector('[data-fpl-swatch]');
        if (sw) activeColor = (sw.dataset.color || '').toLowerCase().trim();
      }

      // Unique sizes in variant order; prefer an available variant per size.
      const seen = {};
      const rows = [];
      info.variants.forEach((v) => {
        if (info.color_index >= 0 && activeColor) {
          const vc = (v.options[info.color_index] || '').toLowerCase().trim();
          if (vc !== activeColor) return;
        }
        const size = v.options[info.size_index];
        if (seen[size]) {
          if (v.available && !seen[size].available) { seen[size].id = v.id; seen[size].available = true; }
          return;
        }
        seen[size] = { size: size, id: v.id, available: !!v.available };
        rows.push(seen[size]);
      });

      const ADD_SVG = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v6M5 8h6"/></svg>';
      const CHEV_SVG = '<svg class="fp__sheet-row-status-chev" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6l5 4 5-4"/></svg>';
      listEl.innerHTML = rows.map((r) => {
        if (r.available) {
          return '<div class="fp__sheet-cell">' +
            '<button type="button" class="fp__sheet-row" data-fpl-qa-pick data-variant-id="' + r.id + '">' +
              '<span class="fp__sheet-row-name">' + esc(r.size) + '</span>' +
              '<span class="fp__sheet-row-status">Add to bag ' + ADD_SVG + '</span>' +
            '</button></div>';
        }
        return '<div class="fp__sheet-cell is-oos">' +
          '<button type="button" class="fp__sheet-row is-unavailable" data-fpl-qa-oos data-url="' + esc(url) + '">' +
            '<span class="fp__sheet-row-name">' + esc(r.size) + '</span>' +
            '<span class="fp__sheet-row-status fp__sheet-row-status--oos"><span class="fp__sheet-row-status-text">Notify when back</span>' + CHEV_SVG + '</span>' +
          '</button></div>';
      }).join('');
      openSheet();
    });

    sheet.addEventListener('click', (e) => { if (e.target.closest('[data-fpl-qa-close]')) closeSheet(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sheet.classList.contains('is-open')) closeSheet(); });

    // Sold-out size → go to the product page (the full back-in-stock notify flow lives there).
    sheet.addEventListener('click', (e) => {
      const oos = e.target.closest('[data-fpl-qa-oos]');
      if (oos && oos.dataset.url) window.location.href = oos.dataset.url;
    });

    // Size pick → add to cart (same flow as the desktop pills).
    sheet.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-fpl-qa-pick]');
      if (!btn || btn.classList.contains('is-soldout') || btn.classList.contains('is-loading')) return;
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      btn.classList.add('is-loading');
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }
        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
          body: formData,
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message || 'Add failed');
        btn.classList.remove('is-loading');
        closeSheet();
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (typeof cart.renderContents === 'function') cart.renderContents(data);
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'fpl-mobile-quickadd', productVariantId: variantId, cartData: data });
        }
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.style.borderColor = 'var(--fpl-blood, #ff0033)';
        setTimeout(() => { btn.style.borderColor = ''; }, 1500);
        console.error('PLP mobile quick-add failed:', err);
      }
    });
  })();

  // ────────────────────────────────────────────────────────────
  // POPSTATE — back/forward between FILTER/SORT states should re-fetch.
  //
  // But it must NOT re-fetch on a back-navigation that didn't change the
  // query — e.g. returning from a product page. That case restores the
  // collection from bfcache (full DOM + scroll position intact) yet still
  // fires popstate; re-fetching here rebuilds the grid via `innerHTML =`,
  // which momentarily empties the container, collapses the document height,
  // and the browser clamps scroll to the top. (Card heights are reserved via
  // padding-bottom, so the collapse is the empty-container instant, not the
  // images — either way the restored scroll position is lost.)
  //
  // Guard: only re-fetch when the FILTER STATE actually differs from what we
  // last rendered (compared order-independently). Same state => bfcache/native
  // scroll restoration is already correct, so leave it alone.
  // ────────────────────────────────────────────────────────────
  window.addEventListener('popstate', () => {
    if (canonicalSearch(window.location.search) === lastRenderedSearch) return;
    const params = new URLSearchParams(window.location.search);
    fetchAndReplace(params, { pushState: false, scrollToTop: false });
  });
})();
